import { quat, vec3 } from 'gl-matrix';

/**
 * Orbit-camera parameters for the ROI 3D preview. The camera always looks at
 * the origin of the ROI's local box (see {@link RoiPreviewRenderer}); only the
 * viewing direction and the eye distance change.
 */
export interface OrbitView {
    azimuthDeg: number;
    elevationDeg: number;
    /** Eye distance from the box centre, in mm. */
    distanceMm: number;
    /**
     * In-plane roll about the view axis, in degrees. Spins the ROI in place
     * without moving the eye (a third rotational DOF distinct from orbiting).
     * Optional and defaults to 0 (no roll).
     */
    rollDeg?: number;
}

/** Orthonormal eye basis in the ROI's local mm space (box centred on origin). */
export interface EyeBasis {
    eye: vec3;
    right: vec3;
    up: vec3;
    forward: vec3;
}

/**
 * Convert orbit parameters into an orthonormal eye basis. Pure function (no
 * side effects) so it can be unit tested independently of WebGL.
 *
 * Azimuth and elevation are both unbounded so the user can orbit a full 360°
 * around the ROI, including straight over the poles. To make that safe, the
 * basis is built WITHOUT the usual `cross(forward, worldUp)` — that product
 * collapses to zero when the view direction is vertical (gimbal flip). Instead
 * `right` is the azimuth tangent `(cos az, 0, -sin az)`, which is unit length
 * for every angle, so the basis stays orthonormal and continuous even at and
 * beyond the poles. Within ±90° elevation this yields the same basis as the
 * world-up cross product.
 */
export function orbitToEyeBasis(view: OrbitView): EyeBasis {
    const azimuth = (view.azimuthDeg * Math.PI) / 180;
    const elevation = (view.elevationDeg * Math.PI) / 180;

    // Unit direction from the box centre to the eye (spherical coordinates).
    const direction = vec3.fromValues(
        Math.cos(elevation) * Math.sin(azimuth),
        Math.sin(elevation),
        Math.cos(elevation) * Math.cos(azimuth)
    );
    const eye = vec3.scale(vec3.create(), direction, view.distanceMm);

    // The camera looks back at the origin, so forward = -direction.
    const forward = vec3.negate(vec3.create(), direction);
    // Azimuth tangent: pole-safe (never zero), so no gimbal flip at the poles.
    const right = vec3.fromValues(Math.cos(azimuth), 0, -Math.sin(azimuth));
    const up = vec3.cross(vec3.create(), right, forward);

    // In-plane roll: spin right/up about the (unchanged) view axis so the ROI
    // rotates in place rather than the eye moving to a new vantage point.
    if (view.rollDeg) {
        const roll = (view.rollDeg * Math.PI) / 180;
        const rollQuat = quat.setAxisAngle(quat.create(), forward, roll);
        vec3.transformQuat(right, right, rollQuat);
        vec3.transformQuat(up, up, rollQuat);
    }

    return { eye, right, up, forward };
}
